import { useCallback, useEffect, useState } from "react";
import {
  createManufacturingPlant,
  deleteManufacturingPlant,
  listManufacturingPlants,
  setDefaultManufacturingPlant,
  updateManufacturingPlant,
  type MfgPlant,
} from "@/lib/manufacturingClient";

export type PlantEditDraft = {
  // D3l: plant_code is editable so orgs can rename the seeded 'DEFAULT'
  // placeholder to a real code without losing FK references (materials /
  // units / recipes that already point at it stay valid through a rename).
  plant_code: string;
  name: string;
  notes: string;
  delivery_address: string;
};

export function useManufacturingPlantsTab(plantsNavTick: number, plantsNewSignal: number) {
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newDeliveryAddress, setNewDeliveryAddress] = useState("");
  const [newIsDefault, setNewIsDefault] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<PlantEditDraft>({
    plant_code: "",
    name: "",
    notes: "",
    delivery_address: "",
  });

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const res = await listManufacturingPlants();
    setListBusy(false);
    if (!res.ok) {
      setPlants([]);
      setLocalError(res.error);
      return;
    }
    setPlants(Array.isArray(res.data.plants) ? res.data.plants : []);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, plantsNavTick]);

  useEffect(() => {
    if (plantsNewSignal === 0) return;
    setCreating(true);
    setEditingId(null);
    setNewCode("");
    setNewName("");
    setNewNotes("");
    setNewIsDefault(false);
    setLocalError(null);
  }, [plantsNewSignal]);

  const cancelCreate = useCallback(() => {
    setCreating(false);
    setLocalError(null);
  }, []);

  const submitCreate = useCallback(async () => {
    const plant_code = newCode.trim();
    const name = newName.trim();
    if (!plant_code || !name) {
      setLocalError("Plant code and name are required.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await createManufacturingPlant({
      plant_code,
      name,
      notes: newNotes.trim() || undefined,
      delivery_address: newDeliveryAddress.trim() || undefined,
      is_default: newIsDefault || undefined,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setCreating(false);
    await reload();
  }, [newCode, newName, newNotes, newDeliveryAddress, newIsDefault, reload]);

  const startEdit = useCallback((plant: MfgPlant) => {
    setEditingId(plant.id);
    setEdit({
      plant_code: plant.plant_code,
      name: plant.name,
      notes: plant.notes,
      delivery_address: plant.delivery_address ?? "",
    });
    setLocalError(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setLocalError(null);
  }, []);

  const submitEdit = useCallback(async () => {
    if (!editingId) return;
    const plant_code = edit.plant_code.trim();
    const name = edit.name.trim();
    if (!plant_code) {
      setLocalError("Plant code cannot be empty.");
      return;
    }
    if (!name) {
      setLocalError("Name cannot be empty.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await updateManufacturingPlant({
      plant_id: editingId,
      plant_code,
      name,
      notes: edit.notes,
      delivery_address: edit.delivery_address,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setEditingId(null);
    await reload();
  }, [editingId, edit, reload]);

  const promoteToDefault = useCallback(
    async (plantId: string) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await setDefaultManufacturingPlant({ plant_id: plantId });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  const toggleActive = useCallback(
    async (plant: MfgPlant) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await updateManufacturingPlant({
        plant_id: plant.id,
        is_active: !plant.is_active,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reload();
    },
    [reload]
  );

  // Slice 7: hard-delete a non-default plant. Returns ok / error so
  // the caller (the confirm modal) can stay open with an inline error
  // when the backend refuses (e.g. FK constraint still references this
  // plant from materials / dealer settings / etc.).
  const deletePlant = useCallback(
    async (
      plant: MfgPlant
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      setActionBusy(true);
      setLocalError(null);
      const res = await deleteManufacturingPlant({ plant_id: plant.id });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return { ok: false, error: res.error };
      }
      await reload();
      return { ok: true };
    },
    [reload]
  );

  return {
    plants,
    listBusy,
    actionBusy,
    localError,
    creating,
    newCode,
    setNewCode,
    newName,
    setNewName,
    newNotes,
    setNewNotes,
    newDeliveryAddress,
    setNewDeliveryAddress,
    newIsDefault,
    setNewIsDefault,
    cancelCreate,
    submitCreate,
    editingId,
    edit,
    setEdit,
    startEdit,
    cancelEdit,
    submitEdit,
    promoteToDefault,
    toggleActive,
    deletePlant,
    reload,
  };
}
